import React from "react";
import { Input as AntInput } from "antd";
import type { InputProps as AntInputProps, InputRef } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { ChevronDown, Search, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import {
  Tooltip,
  type TooltipPlacement,
  type TooltipVariant,
} from "../tool-tip";

const { Password, TextArea } = AntInput;

// ============================================
// Types
// ============================================

export type InputSize = "small" | "medium" | "large";
export type InputVariant = "outlined" | "filled" | "borderless";

export interface InputProps extends Omit<AntInputProps, "size"> {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
  type?: "text" | "password" | "email" | "number" | "tel" | "url";
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  allowClear?: boolean;
  className?: string;
  /** Tooltip content for label */
  tooltip?: React.ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: TooltipPlacement;
  /** Tooltip variant */
  tooltipVariant?: TooltipVariant;
}

export interface PasswordInputProps extends Omit<InputProps, "type"> {
  visibilityToggle?: boolean;
}

export interface TextAreaProps {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  maxLength?: number;
  showCount?: boolean;
  /** Position the character count inside the textarea box (bottom-right) */
  countInside?: boolean;
  className?: string;
  id?: string;
  /** Whether to hide the border (useful when wrapping in a custom container) */
  borderless?: boolean;
  /** Custom wrapper style */
  wrapperStyle?: React.CSSProperties;
  /** Custom wrapper className */
  wrapperClassName?: string;
}

export interface PhoneInputCountry {
  code: string;
  dial_code: string;
  name: string;
  flag?: string;
}

export interface PhoneInputProps {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  countryCode?: string;
  defaultCountryCode?: string;
  onChange?: (value: string) => void;
  onCountryChange?: (country: PhoneInputCountry) => void;
  countries: PhoneInputCountry[];
  className?: string;
  searchable?: boolean;
  renderFlag?: (countryCode: string) => React.ReactNode;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
}

// ============================================
// Size Configuration
// ============================================
const sizeConfig = {
  small: { height: "2.5rem", fontSize: "0.875rem", px: "px-3" },
  medium: { height: "2.5rem", fontSize: "0.9375rem", px: "px-3" },
  large: { height: "3rem", fontSize: "1rem", px: "px-4" },
} as const;

// ============================================
// Shared Components
// ============================================
interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
  tooltipVariant?: TooltipVariant;
}

const Label: React.FC<LabelProps> = ({
  children,
  required,
  htmlFor,
  tooltip,
  tooltipPlacement = "top",
  tooltipVariant = "default",
}) => (
  <label
    htmlFor={htmlFor}
    className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
    style={{ color: colors.textDefault }}
  >
    <span>{children}</span>
    {required && <span style={{ color: colors.error }}>*</span>}
    {tooltip && (
      <Tooltip
        title={tooltip}
        placement={tooltipPlacement}
        variant={tooltipVariant}
      >
        <span
          className="inline-flex items-center cursor-help"
          style={{ color: colors.textMuted }}
        >
          <Info size={14} />
        </span>
      </Tooltip>
    )}
  </label>
);

interface HelperTextProps {
  children: React.ReactNode;
  isError?: boolean;
}

const HelperText: React.FC<HelperTextProps> = ({ children, isError }) => (
  <div
    className="mt-1 text-xs"
    style={{ color: isError ? colors.error : colors.textMuted }}
  >
    {children}
  </div>
);

// ============================================
// Input Component
// ============================================

export const Input = React.forwardRef<InputRef, InputProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      size = "medium",
      variant = "filled",
      fullWidth = true,
      type = "text",
      placeholder,
      disabled = false,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      prefix,
      suffix,
      allowClear = false,
      className = "",
      style,
      tooltip,
      tooltipPlacement,
      tooltipVariant,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || ""
    );
    const currentValue = value !== undefined ? value : internalValue;
    const isFilled = Boolean(currentValue && String(currentValue).length > 0);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    // Determine border color based on state
    const getBorderColor = () => {
      if (error) return colors.error;
      if (isFocused) return colors.primary;
      if (isFilled) return colors.surfaceDark;
      return colors.surface;
    };

    // Determine background color based on state
    const getBackgroundColor = () => {
      if (disabled) return colors.surfaceLighter;
      if (isFocused) return colors.white;
      return variant === "filled" ? colors.surfaceLight : colors.white;
    };

    const inputStyle: React.CSSProperties = {
      height: sizeConfig[size].height,
      fontSize: sizeConfig[size].fontSize,
      borderColor: variant === "borderless" ? "transparent" : getBorderColor(),
      borderWidth: variant === "borderless" ? 0 : isFocused || error ? 2 : 1,
      backgroundColor:
        variant === "borderless" ? "transparent" : getBackgroundColor(),
      borderRadius: 3,
      boxShadow: "none",
      transition: "border-color 0.15s ease, background-color 0.15s ease",
      ...style,
    };

    return (
      <div className={cn(fullWidth && "w-full", className)}>
        {label && (
          <Label
            required={required}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            tooltipVariant={tooltipVariant}
          >
            {label}
          </Label>
        )}
        <AntInput
          ref={ref}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          prefix={prefix}
          suffix={suffix}
          allowClear={allowClear}
          variant={variant}
          status={error ? "error" : undefined}
          style={inputStyle}
          {...props}
        />
        {helperText && !error && <HelperText>{helperText}</HelperText>}
        {error && <HelperText isError>{error}</HelperText>}
      </div>
    );
  }
);

Input.displayName = "Input";

// ============================================
// PasswordInput Component
// ============================================

export const PasswordInput = React.forwardRef<InputRef, PasswordInputProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      size = "medium",
      variant = "filled",
      fullWidth = true,
      placeholder,
      disabled = false,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      prefix,
      allowClear = false,
      visibilityToggle = true,
      className = "",
      style,
      tooltip,
      tooltipPlacement,
      tooltipVariant,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || ""
    );
    const currentValue = value !== undefined ? value : internalValue;
    const isFilled = Boolean(currentValue && String(currentValue).length > 0);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const getBorderColor = () => {
      if (error) return colors.error;
      if (isFocused) return colors.primary;
      if (isFilled) return colors.surfaceDark;
      return colors.surface;
    };

    const getBackgroundColor = () => {
      if (disabled) return colors.surfaceLighter;
      if (isFocused) return colors.white;
      return variant === "filled" ? colors.surfaceLight : colors.white;
    };

    const inputStyle: React.CSSProperties = {
      height: sizeConfig[size].height,
      fontSize: sizeConfig[size].fontSize,
      borderColor: getBorderColor(),
      borderWidth: isFocused || error ? 2 : 1,
      backgroundColor: getBackgroundColor(),
      borderRadius: 3,
      boxShadow: "none",
      transition: "border-color 0.15s ease, background-color 0.15s ease",
      ...style,
    };

    return (
      <div className={cn(fullWidth && "w-full", className)}>
        {label && (
          <Label
            required={required}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            tooltipVariant={tooltipVariant}
          >
            {label}
          </Label>
        )}
        <Password
          ref={ref}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          prefix={prefix}
          allowClear={allowClear}
          variant={variant}
          visibilityToggle={visibilityToggle}
          iconRender={(visible) =>
            visible ? (
              <EyeOutlined style={{ color: colors.textMuted }} />
            ) : (
              <EyeInvisibleOutlined style={{ color: colors.textMuted }} />
            )
          }
          status={error ? "error" : undefined}
          style={inputStyle}
          {...props}
        />
        {helperText && !error && <HelperText>{helperText}</HelperText>}
        {error && <HelperText isError>{error}</HelperText>}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

// ============================================
// PhoneInput Component
// ============================================

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  required = false,
  helperText,
  error,
  size = "large",
  fullWidth = true,
  placeholder = "Enter phone number",
  disabled = false,
  value = "",
  countryCode,
  defaultCountryCode = "+91",
  onChange,
  onCountryChange,
  countries,
  className = "",
  searchable = true,
  renderFlag,
  onKeyDown,
  autoFocus = false,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = React.useRef<InputRef>(null);

  // Auto focus effect
  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const selectedCountryCode = countryCode || defaultCountryCode;
  const selectedCountry =
    countries.find((c) => c.dial_code === selectedCountryCode) || countries[0];

  const filteredCountries = React.useMemo(() => {
    if (!searchQuery) return countries;
    const query = searchQuery.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dial_code.includes(query) ||
        country.code.toLowerCase().includes(query)
    );
  }, [countries, searchQuery]);

  // Reset highlight when filtered list changes
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredCountries.length, searchQuery]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = React.useCallback(
    (country: PhoneInputCountry) => {
      onCountryChange?.(country);
      setIsDropdownOpen(false);
      setSearchQuery("");
      setHighlightedIndex(-1);
    },
    [onCountryChange],
  );

  // Keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isDropdownOpen) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredCountries.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCountries.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredCountries.length
          ) {
            handleCountrySelect(filteredCountries[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsDropdownOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
          break;
      }
    },
    [isDropdownOpen, filteredCountries, highlightedIndex, handleCountrySelect]
  );

  const isFilled = Boolean(value && value.length > 0);
  const isActive = isFocused || isDropdownOpen;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, "");
    onChange?.(numericValue);
  };

  // Default flag renderer using emoji
  const defaultRenderFlag = (code: string) => {
    const codePoints = code
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return (
      <span className="text-lg">{String.fromCodePoint(...codePoints)}</span>
    );
  };

  const flagRenderer = renderFlag || defaultRenderFlag;

  // Compute styles
  const getBorderColor = () => {
    if (error) return colors.error;
    if (isActive) return colors.primary;
    if (isFilled) return colors.surfaceDark;
    return colors.surface;
  };

  const getBackgroundColor = () => {
    if (disabled) return colors.surfaceLighter;
    if (isActive) return colors.white;
    return colors.surfaceLight;
  };

  const containerStyle: React.CSSProperties = {
    borderColor: getBorderColor(),
    borderWidth: isActive || error ? 2 : 1,
    backgroundColor: getBackgroundColor(),
    borderRadius: 3,
    borderStyle: "solid",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "text",
  };

  return (
    <div className={cn(fullWidth && "w-full", className)} ref={containerRef}>
      {label && <Label required={required}>{label}</Label>}

      <div
        className="flex items-center relative"
        style={{ ...containerStyle, height: sizeConfig[size].height }}
      >
        {/* Country Code Selector */}
        <div
          className={cn(
            "flex items-center gap-1.5 h-full shrink-0 select-none",
            sizeConfig[size].px,
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          style={{ borderRight: `1px solid ${colors.surfaceSubtle}` }}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setIsDropdownOpen(!isDropdownOpen);
          }}
        >
          {selectedCountry && flagRenderer(selectedCountry.code)}
          <span
            className="font-medium"
            style={{
              color: colors.textInverse,
              fontSize: sizeConfig[size].fontSize,
            }}
          >
            {selectedCountry?.dial_code}
          </span>
          <ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-200",
              isDropdownOpen && "rotate-180"
            )}
            style={{ color: colors.textMuted }}
          />
        </div>

        {/* Phone Number Input */}
        <AntInput
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="borderless"
          className="flex-1 h-full"
          style={{
            color: colors.textInverse,
            backgroundColor: "transparent",
            fontSize: sizeConfig[size].fontSize,
          }}
        />

        {/* Dropdown */}
        {isDropdownOpen && (
          <div
            className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md overflow-hidden"
            style={{
              backgroundColor: colors.white,
              border: `1px solid ${colors.surfaceSubtle}`,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              maxHeight: searchable ? 262 : 210,
              overflowY: "auto",
            }}
          >
            {searchable && (
              <div
                className="sticky top-0 p-2"
                style={{
                  backgroundColor: colors.white,
                  borderBottom: `1px solid ${colors.surfaceSubtle}`,
                }}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded"
                  style={{
                    border: `1px solid ${colors.surfaceSubtle}`,
                    backgroundColor: colors.white,
                  }}
                >
                  <Search
                    size={16}
                    className="shrink-0"
                    style={{ color: colors.textMuted }}
                  />
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 border-none outline-none text-sm bg-transparent"
                    style={{ color: colors.textInverse }}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {filteredCountries.map((country, index) => {
              const isSelected =
                country.dial_code === selectedCountry?.dial_code;
              const isHighlighted = index === highlightedIndex;

              let bgColor = "transparent";
              if (isHighlighted) bgColor = colors.surfaceLighter;
              else if (isSelected) bgColor = colors.primaryExtraLight;

              return (
                <div
                  key={`${country.code}-${country.dial_code}`}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-150"
                  style={{ backgroundColor: bgColor }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => {
                    if (highlightedIndex === index) setHighlightedIndex(-1);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCountrySelect(country);
                  }}
                >
                  {flagRenderer(country.code)}
                  <span
                    className="flex-1 text-sm"
                    style={{ color: colors.textInverse }}
                  >
                    {country.name}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.textMuted }}
                  >
                    {country.dial_code}
                  </span>
                </div>
              );
            })}

            {filteredCountries.length === 0 && (
              <div
                className="py-4 px-3 text-center text-sm"
                style={{ color: colors.textMuted }}
              >
                No countries found
              </div>
            )}
          </div>
        )}
      </div>

      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {error && <HelperText isError>{error}</HelperText>}
    </div>
  );
};

PhoneInput.displayName = "PhoneInput";

// ============================================
// TextAreaInput Component
// ============================================

export const TextAreaInput: React.FC<TextAreaProps> = ({
  label,
  required = false,
  helperText,
  error,
  size = "medium",
  variant = "filled",
  fullWidth = true,
  placeholder,
  disabled = false,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  rows = 4,
  autoSize,
  maxLength,
  showCount = false,
  countInside = false,
  className = "",
  id,
  borderless = false,
  wrapperStyle,
  wrapperClassName = "",
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const currentValue = value !== undefined ? value : internalValue;
  const isFilled = Boolean(currentValue && String(currentValue).length > 0);
  const currentLength = String(currentValue || "").length;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (value === undefined) {
      setInternalValue(e.target.value);
    }
    onChange?.(e);
  };

  const getBorderColor = () => {
    if (borderless) return "transparent";
    if (error) return colors.error;
    if (isFocused) return colors.primary;
    if (isFilled) return colors.surfaceDark;
    return colors.surface;
  };

  const getBackgroundColor = () => {
    if (disabled) return colors.surfaceLighter;
    if (isFocused) return colors.white;
    return variant === "filled" ? colors.surfaceLight : colors.white;
  };

  const getFontSize = () => {
    switch (size) {
      case "small":
        return "0.875rem";
      case "large":
        return "1rem";
      default:
        return "0.9375rem";
    }
  };

  // When countInside is true, we need to handle count display ourselves
  const shouldShowCountInside = showCount && countInside && maxLength;
  const shouldShowCountOutside = showCount && !countInside;
  const currentBorderWidth = borderless ? 0 : isFocused || error ? 2 : 1;

  const textAreaStyle: React.CSSProperties = {
    fontSize: getFontSize(),
    borderColor: shouldShowCountInside ? "transparent" : getBorderColor(),
    borderWidth: shouldShowCountInside ? 0 : currentBorderWidth,
    backgroundColor: getBackgroundColor(),
    borderRadius: shouldShowCountInside ? 0 : 3,
    boxShadow: "none",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    // Add padding at bottom for inside count
    ...(shouldShowCountInside && { paddingBottom: 24 }),
  };

  const containerStyle: React.CSSProperties = {
    position: "relative",
    ...(shouldShowCountInside && {
      borderColor: getBorderColor(),
      borderWidth: currentBorderWidth,
      borderStyle: "solid",
      borderRadius: 3,
      overflow: "hidden",
      backgroundColor: getBackgroundColor(),
      transition: "border-color 0.15s ease, background-color 0.15s ease",
    }),
    ...wrapperStyle,
  };

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      {label && <Label required={required}>{label}</Label>}
      <div className={wrapperClassName} style={containerStyle}>
        <TextArea
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          rows={rows}
          autoSize={autoSize}
          maxLength={maxLength}
          showCount={shouldShowCountOutside}
          status={error ? "error" : undefined}
          style={textAreaStyle}
        />
        {shouldShowCountInside && (
          <div
            className="absolute bottom-0 right-0 px-3 py-1 text-xs pointer-events-none"
            style={{
              color: colors.textMuted,
            }}
          >
            {currentLength} / {maxLength}
          </div>
        )}
      </div>
      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {error && <HelperText isError>{error}</HelperText>}
    </div>
  );
};

TextAreaInput.displayName = "TextAreaInput";

export default Input;
